
import React, { useState, useEffect } from 'react';
import { Mic, MicOff, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

interface SpeechToTextInputProps {
  onTranscript: (text: string) => void;
}

const SpeechToTextInput: React.FC<SpeechToTextInputProps> = ({ onTranscript }) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [supported, setSupported] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  useEffect(() => {
    // Check if browser supports Web Speech API
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'no-NO'; // Set to Norwegian

      recognitionInstance.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0])
          .map((result: any) => result.transcript)
          .join('');
        
        onTranscript(transcript);
        
        // Reset error states when we get successful results
        if (networkError) setNetworkError(false);
        if (errorCount > 0) setErrorCount(0);
      };

      recognitionInstance.onerror = (event: any) => {
        console.error('Speech recognition error', event);
        
        // Handle different error types
        if (event.error === 'network') {
          setNetworkError(true);
          toast.error('Nettverksfeil: Sørg for at du er tilkoblet internett');
          setIsListening(false);
        } else if (event.error === 'not-allowed') {
          toast.error('Talegjenkjenning er ikke tillatt. Sjekk mikrofoninnstillinger.');
          setIsListening(false);
        } else if (event.error === 'no-speech') {
          // This is common and doesn't need a toast notification every time
          console.log('No speech detected');
        } else {
          // Increment error count for other errors
          setErrorCount(prev => prev + 1);
          
          // Only show toast for the first few errors to avoid spamming
          if (errorCount < 3) {
            toast.error(`Det oppstod en feil med talegjenkjenning: ${event.error}`);
          }
          
          // If we get too many errors, stop the recognition
          if (errorCount >= 5) {
            setIsListening(false);
            toast.error('For mange feil oppstod. Talegjenkjenning stoppet.');
          }
        }
      };

      recognitionInstance.onend = () => {
        if (isListening && !networkError) {
          // Restart recognition if it was supposed to be running and no network error
          try {
            recognitionInstance.start();
          } catch (e) {
            console.error('Failed to restart recognition:', e);
            setIsListening(false);
            toast.error('Kunne ikke gjenoppta talegjenkjenning');
          }
        } else {
          setIsListening(false);
        }
      };

      setRecognition(recognitionInstance);
    } else {
      setSupported(false);
      console.warn('Speech recognition not supported');
    }

    return () => {
      if (recognition) {
        try {
          recognition.stop();
        } catch (e) {
          console.error('Error stopping recognition on unmount:', e);
        }
      }
    };
  }, [errorCount, isListening, networkError]);

  const toggleListening = () => {
    if (!supported) {
      toast.error('Talegjenkjenning støttes ikke i denne nettleseren');
      return;
    }

    if (networkError) {
      // Try to reset network error state
      setNetworkError(false);
      setErrorCount(0);
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
      toast.info('Talegjenkjenning avsluttet');
    } else {
      try {
        recognition.start();
        setIsListening(true);
        toast.success('Talegjenkjenning aktivert - snakk nå');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Kunne ikke starte talegjenkjenning');
      }
    }
  };

  if (!supported) {
    return (
      <button
        type="button"
        className="flex items-center gap-2 text-sm text-gray-500 opacity-50 cursor-not-allowed"
        disabled
      >
        <MicOff size={16} />
        <span>Talegjenkjenning støttes ikke</span>
      </button>
    );
  }

  if (networkError) {
    return (
      <button
        type="button"
        onClick={toggleListening}
        className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors"
      >
        <WifiOff size={16} />
        <span>Nettverksfeil - Klikk for å prøve igjen</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggleListening}
      className={`flex items-center gap-2 text-sm ${
        isListening ? 'text-green-600' : 'text-gray-700'
      } hover:text-green-700 transition-colors`}
    >
      {isListening ? <Mic size={16} className="animate-pulse" /> : <Mic size={16} />}
      <span>{isListening ? 'Stopp opptak' : 'Diktér svaret ditt'}</span>
    </button>
  );
};

export default SpeechToTextInput;
